import ApiHandler from '@/logic/ApiHandler';
import ApiRoutes from '@/logic/ApiRoutes';
import {DisplayItem, DICONSTS} from '@/types/DisplayItem';

export const performance = {
    namespaced: true,
    state: {
        displayItems: Array<DisplayItem>(),
    },
    getters: {

        // Return the ID of the last partition
        recentPartitionID: (state: any) => {

            // Go through the list from the back
            for (let index = state.displayItems.length - 1; index >= 0 ; index--) {
                if (state.displayItems[index].type === 'partition') {
                    return index;
                }
            }

            // Not found
            return -1;
        },

        // Return display chunks up to the last partition
        recentDisplayChunks: (state: any, getters: any) => {

            // Get last partition
            const index: number = getters.recentPartitionID;

            if (index === -1) {
                // No partitions sent yet
                return state.displayItems;
            } else {
                return state.displayItems.slice(index + 1, state.displayItems.length );
            }
        },

        // Return the last partition
        recentPartition: (state: any, getters: any) => {

            // Get last partition
            const index: number = getters.recentPartitionID;

            if (index === -1) {
                return null;
            } else {
                return state.displayItems[getters.recentPartitionID];
            }
        },

        // Return display items from before the last partition chunk
        getPreviousDisplayChunks: (state: any, getters: any) => {

            // Get last partition
            const index: number = getters.recentPartitionID;

            if (index === -1) {
                // No partitions sent yet
                return state.displayItems;
            } else {
                return state.displayItems.slice(0, index);
            }
        },

        // Return all partitions as (partition, index) tuples
        getPartitions: (state: any) => {

            const partitions: Array<[DisplayItem, number]> = [];

            for (let i: number = 0; i < state.displayItems.length; i++) {

                if (state.displayItems[i].type === DICONSTS.DI_TYPE_PARTITION) {
                    partitions.push([state.displayItems[i], i]);
                }
            }

            return partitions;
        },
        getPartitionChunks: (state: any, getters: any) => (index: number) => {

            const items: DisplayItem[] = [];

            for (let i: number = index - 1; i >= 0 ; i--) {
                const item = state.displayItems[i];

                if (item.type === DICONSTS.DI_TYPE_PARTITION) {
                    break;
                }
                items.push(item);
            }

            return items.reverse();
        },
        getNumDisplayItems: (state: any) => {
            return state.displayItems.length;
        }
    },
    actions: {
        async fetchDisplayItems(context: any) {
            const profileId: number = context.rootGetters['settings/getProfile'];
            const performanceId: number = context.rootGetters['settings/getPerformance'];

            const request = ApiRoutes.getLiveFeedRequest(performanceId, profileId);
            const data: DisplayItem[] = await ApiHandler.request(request);

            if (data.length === 0) {
                context.commit('clearDisplayItems');
                return;
            }

            // Assign id's if not present
            if (!data[0].id) {

                context.dispatch('analytics/logWarning', {
                    message: 'No id present on displayitems',
                    details: JSON.stringify(data)
                }, {root: true});

                for (let index = 0; index < data.length; index++) {
                    data[index].id = index;
                }
            }

            // Reverse list
            // TODO: Reverse in backend
            data.reverse();

            context.commit('setDisplayItems', data);
        },
        receiveDisplayItem(context: any, displayItem: any) {
            displayItem = JSON.parse(displayItem);

            let duplicate = false;

            // Add an id if there isn't one present
            if (!displayItem.id) {
                displayItem.id = context.state.displayItems.length;
                context.dispatch('analytics/logWarning', {
                    message: 'No id present on displayitem',
                    details: JSON.stringify(displayItem)
                }, {root: true});
            } else {

                // block duplicates
                context.state.displayItems.forEach((el) => {
                    // Only if both di type and id match
                    if (el.type === displayItem.type && el.id === displayItem.id) {
                        duplicate = true;

                        context.dispatch('analytics/logWarning', {
                            message: 'Blocked duplicate Displayitem',
                            details: JSON.stringify(displayItem)
                        }, {root: true});
                    }
                });

            }

            if (!duplicate) {
                context.commit('addDisplayItem', displayItem);
            }
        },
        retractDisplayItem(context: any, displayItem: any) {
            const item: DisplayItem = JSON.parse(displayItem);


            // For instant messages, the chunk can be anywhere
            if (item.type === DICONSTS.DI_TYPE_INSTANT) {

                const toRemove = context.state.displayItems.find(
                    (di) => di.type === DICONSTS.DI_TYPE_INSTANT && di.id === item.id);

                if (toRemove !== undefined) {
                    context.commit('removeDisplayItem', toRemove);
                    return;
                } else {
                    context.dispatch('analytics/logWarning', {
                        message: 'Could not find retrated instant message',
                        details: JSON.stringify(displayItem)
                    }, {root: true});
                }
            }

            if (context.state.displayItems.length > 1) {
                // Check id matches latest item
                if (! (context.state.displayItems[context.state.displayItems.length - 1].id === item.id)) {
                    context.dispatch('analytics/logWarning', {
                        message: 'Retracted displayitem does not match id',
                        details: JSON.stringify(displayItem)
                    }, {root: true});
                }

                if (context.state.displayItems[context.state.displayItems.length - 2]
                    .type === DICONSTS.DI_TYPE_PARTITION ) {

                    context.commit('removeLastDisplayItems', 2);
                } else {
                    context.commit('removeLastDisplayItems', 1);
                }
            } else {
                context.commit('removeLastDisplayItems', 1);
            }
        }
    },
    mutations: {
        setDisplayItems(state: any, displayItems: any) {
            state.displayItems = displayItems;
        },
        addDisplayItem(state: any, displayItem: any) {
            state.displayItems.push(displayItem);
        },
        removeLastDisplayItems(state: any, amount: number) {

            state.displayItems.splice(state.displayItems.length - amount);
        },
        removeDisplayItem(state: any, displayItem: any) {

            const index = state.displayItems.indexOf(displayItem);

            if (index !== undefined) {
                state.displayItems.splice(index, 1);
            }
        },
        clearDisplayItems(state: any) {
            state.displayItems = [];
        }
    },
};
